import { useState, useEffect } from 'react';
import {
  XMarkIcon,
  CheckCircleIcon,
  BuildingOffice2Icon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  TrophyIcon,
  SparklesIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { registerPartner, getTournaments } from '../../lib/supabase';
import { toast } from 'react-hot-toast';

const SPONSORSHIP_TIERS = [
  'Tournament Host',
  'Diamond',
  'Platinum',
  'Gold',
  'Silver',
  'Bronze',
  'Official Airline',
  'Official Bank',
  'Official Beverage',
  'Official Telecom Partner',
  'Official Automotive Brand',
  'Official Insurance Partner',
  'Official Real Estate Partner',
  'Official Media Partner',
  'Official Logistics Partner',
  'Official Construction Partner',
  'Official Apparel Brand',
] as const;

const tierContributions: Record<string, number> = {
  Diamond: 26000000, Platinum: 16000000, Gold: 6000000, Silver: 1000000,
  Bronze: 500000, 'Tournament Host': 0, 'Official Airline': 20000000,
  'Official Bank': 20000000, 'Official Beverage': 15000000,
  'Official Telecom Partner': 20000000, 'Official Automotive Brand': 15000000,
  'Official Insurance Partner': 10000000, 'Official Real Estate Partner': 10000000,
  'Official Media Partner': 5000000,
  'Official Logistics Partner': 10000000,
  'Official Construction Partner': 10000000,
  'Official Apparel Brand': 10000000,
};

const tierBenefits: Record<string, string[]> = {
  'Tournament Host': ['Official Host Recognition', 'Venue Branding Rights', 'VIP Hospitality Access', 'Media Recognition', 'Tournament Naming Rights', 'Exclusive Promotion'],
  Diamond: ['Premium Brand Placement', 'VIP Hospitality Suite', 'Pro-Am Team Entry', 'Executive Meet & Greet', 'Custom Brand Integration', 'Category Exclusivity'],
  Platinum: ['Category Exclusivity', 'Brand Activation Space', 'VIP Tournament Access', 'Pro-Am Team Entry', 'Digital Integration', 'Hospitality Package'],
  Gold: ['On-site Branding', 'Digital Presence', 'Event Access', 'Pro-Am Entry', 'Marketing Rights', 'Networking Events'],
  Silver: ['Logo Placement', 'Digital Recognition', 'Event Tickets', 'Networking Access', 'Brand Visibility', 'Media Mentions'],
  Bronze: ['Digital Recognition', 'Event Tickets', 'Brand Listing', 'Website Presence', 'Program Mention', 'Certificate of Appreciation'],
  'Official Airline': ['Exclusive Airline Category Rights', 'Premium Brand Placement', 'VIP Travel Packages', 'Co-branded Marketing', 'Executive Lounge Access', 'Category Exclusivity'],
  'Official Bank': ['Exclusive Banking Category Rights', 'Branded ATM/Payment Points', 'VIP Hospitality Package', 'Financial Services Activation', 'Executive Networking Access', 'Category Exclusivity'],
  'Official Beverage': ['Exclusive Beverage Category Rights', 'On-course Branded Stations', 'Product Sampling Activation', 'VIP Hospitality Inclusion', 'Branded Merchandise Integration', 'Category Exclusivity'],
  'Official Telecom Partner': ['Exclusive Telecom Category Rights', 'Connectivity Sponsorship Branding', 'Digital Campaign Integration', 'VIP Hospitality Package', 'App & Social Media Features', 'Category Exclusivity'],
  'Official Automotive Brand': ['Exclusive Automotive Category Rights', 'Vehicle Fleet Branding', 'Hole-in-One Prize Sponsorship', 'VIP Chauffeur Activation', 'On-site Vehicle Display', 'Category Exclusivity'],
  'Official Insurance Partner': ['Exclusive Insurance Category Rights', 'Player & Event Coverage Branding', 'Risk Partner Recognition', 'VIP Hospitality Package', 'Co-branded Communications', 'Category Exclusivity'],
  'Official Real Estate Partner': ['Exclusive Real Estate Category Rights', 'Premium Hoarding & Signage', 'Lifestyle Brand Activation', 'VIP Hospitality Suite Access', 'Co-branded Property Marketing', 'Category Exclusivity'],
  'Official Media Partner': ['Exclusive Media Category Rights', 'Broadcast & Content Partnership', 'Social Media Co-promotion', 'Press Conference Branding', 'Highlight Reel Sponsorship', 'Category Exclusivity'],
  'Official Logistics Partner': ['Exclusive Logistics Category Rights', 'Fleet & Delivery Branding', 'On-site Operations Recognition', 'VIP Hospitality Package', 'Co-branded Logistics Activation', 'Category Exclusivity'],
  'Official Construction Partner': ['Exclusive Construction Category Rights', 'Project Branding Recognition', 'Infrastructure Sponsorship Display', 'VIP Hospitality Package', 'Co-branded Communications', 'Category Exclusivity'],
  'Official Apparel Brand': ['Exclusive Apparel Category Rights', 'Player & Staff Kit Branding', 'On-course Merchandise Activation', 'VIP Hospitality Package', 'Co-branded Fashion Marketing', 'Category Exclusivity'],
};

const tierColors: Record<string, string> = {
  'Tournament Host': 'bg-amber-100 text-amber-800 border-amber-200',
  Diamond: 'bg-sky-100 text-sky-800 border-sky-200',
  Platinum: 'bg-slate-100 text-slate-700 border-slate-200',
  Gold: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  Silver: 'bg-gray-100 text-gray-600 border-gray-200',
  Bronze: 'bg-orange-100 text-orange-700 border-orange-200',
};

const getTierColor = (tier: string) =>
  tierColors[tier] || 'bg-green-100 text-green-800 border-green-200';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

interface FormState {
  companyName: string;
  contactPerson: string;
  email: string;
  phone: string;
  sponsorshipTier: string;
  tournamentId: string;
}

const emptyForm = (): FormState => ({
  companyName: '', contactPerson: '', email: '', phone: '',
  sponsorshipTier: 'Gold', tournamentId: '',
});

export default function MobilePartnerSheet({ isOpen, onClose }: Props) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [tournaments, setTournaments] = useState<{ id: string; name: string }[]>([]);

  useEffect(() => {
    if (isOpen) {
      setError('');
      setDone(false);
      setStep(1);
      setForm(emptyForm());
      getTournaments().then(({ data }) => {
        const upcoming = (data || [])
          .filter((t: any) => t.status === 'upcoming')
          .map((t: any) => ({ id: t.id, name: t.name }));
        setTournaments(upcoming);
        if (upcoming.length > 0) setForm(prev => ({ ...prev, tournamentId: upcoming[0].id }));
      });
    }
  }, [isOpen]);

  const f = (key: keyof FormState, value: string) => setForm(prev => ({ ...prev, [key]: value }));

  const handleNext = () => {
    if (!form.companyName.trim()) { setError('Company name is required'); return; }
    if (!form.contactPerson.trim()) { setError('Contact person is required'); return; }
    if (!form.email.trim()) { setError('Email is required'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) { setError('Please enter a valid email address'); return; }
    if (!form.phone.trim()) { setError('Phone is required'); return; }
    setError('');
    setStep(2);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      const { error: err } = await registerPartner({
        company_name: form.companyName.trim(),
        contact_person: form.contactPerson.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        tier: form.sponsorshipTier,
        status: 'pending',
        contribution: tierContributions[form.sponsorshipTier] || 0,
        start_date: new Date().toISOString().split('T')[0],
        end_date: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
        benefits: tierBenefits[form.sponsorshipTier] || [],
        tournament_id: form.tournamentId,
      });
      if (err) throw err;
      toast.success('Partner interest submitted');
      setDone(true);
    } catch (err: any) {
      setError(err.message || 'Failed to submit');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const inputClass = 'w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-golf-orange bg-gray-50 transition-colors';
  const labelClass = 'block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide';
  const contribution = tierContributions[form.sponsorshipTier];
  const benefits = tierBenefits[form.sponsorshipTier] || [];
  const tierColor = getTierColor(form.sponsorshipTier);

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={!done ? onClose : undefined} />
      <div className="relative bg-white rounded-t-3xl shadow-2xl max-h-[94vh] flex flex-col">

        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-gray-100 flex-shrink-0">
          <div>
            <h2 className="text-base font-bold text-golf-gray">
              {done ? 'Interest Submitted' : 'Partner Interest'}
            </h2>
            {!done && (
              <p className="text-xs text-gray-400 mt-0.5">
                Step {step} of 2 — {step === 1 ? 'Contact Details' : 'Sponsorship Tier'}
              </p>
            )}
          </div>
          {!done && (
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-xl bg-gray-100 active:bg-gray-200 transition-colors"
            >
              <XMarkIcon className="w-4 h-4 text-gray-500" />
            </button>
          )}
        </div>

        {!done && (
          <div className="flex gap-1.5 px-5 pt-3 pb-1 flex-shrink-0">
            {[1, 2].map(s => (
              <div
                key={s}
                className={`h-1 flex-1 rounded-full transition-all duration-300 ${s <= step ? 'bg-golf-orange' : 'bg-gray-200'}`}
              />
            ))}
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {done ? (
            <div className="flex flex-col items-center text-center py-8 space-y-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircleIcon className="w-10 h-10 text-green-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-golf-gray">Submitted Successfully</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Your partner interest has been recorded. Our team will be in touch soon.
                </p>
              </div>
              <div className="bg-golf-sand/40 rounded-2xl px-6 py-4 w-full space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Company</span>
                  <span className="font-semibold text-golf-gray">{form.companyName}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Tier</span>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${getTierColor(form.sponsorshipTier)}`}>
                    {form.sponsorshipTier}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Contact</span>
                  <span className="font-semibold text-golf-gray">{form.contactPerson}</span>
                </div>
              </div>
              <p className="text-xs text-gray-400">A confirmation will be sent to {form.email}</p>
              <button
                onClick={onClose}
                className="w-full py-3 rounded-xl bg-golf-orange text-white font-semibold text-sm mt-2"
              >
                Done
              </button>
            </div>
          ) : step === 1 ? (
            <div className="space-y-5">
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-600">
                  {error}
                </div>
              )}

              {tournaments.length > 0 && (
                <div>
                  <label className={labelClass}>Tournament</label>
                  <div className="relative">
                    <TrophyIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    <select
                      value={form.tournamentId}
                      onChange={e => f('tournamentId', e.target.value)}
                      className={`${inputClass} pl-10 appearance-none`}
                    >
                      {tournaments.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                    <ChevronDownIcon className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  </div>
                </div>
              )}

              <div className="bg-gray-50 rounded-2xl p-4 space-y-4">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide flex items-center gap-1.5">
                  <BuildingOffice2Icon className="w-3.5 h-3.5" /> Company
                </p>
                <div>
                  <label className={labelClass}>Company Name *</label>
                  <div className="relative">
                    <BuildingOffice2Icon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    <input
                      type="text"
                      value={form.companyName}
                      onChange={e => f('companyName', e.target.value)}
                      placeholder="e.g. Acme Corporation"
                      className={`${inputClass} pl-10`}
                    />
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-2xl p-4 space-y-4">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide flex items-center gap-1.5">
                  <UserIcon className="w-3.5 h-3.5" /> Contact
                </p>
                <div>
                  <label className={labelClass}>Contact Person *</label>
                  <div className="relative">
                    <UserIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    <input
                      type="text"
                      value={form.contactPerson}
                      onChange={e => f('contactPerson', e.target.value)}
                      placeholder="Full name"
                      className={`${inputClass} pl-10`}
                    />
                  </div>
                </div>

                <div>
                  <label className={labelClass}>Email *</label>
                  <div className="relative">
                    <EnvelopeIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    <input
                      type="email"
                      value={form.email}
                      onChange={e => f('email', e.target.value)}
                      placeholder="contact@company.com"
                      className={`${inputClass} pl-10`}
                    />
                  </div>
                </div>

                <div>
                  <label className={labelClass}>Phone *</label>
                  <div className="relative">
                    <PhoneIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={e => f('phone', e.target.value)}
                      placeholder="+234..."
                      className={`${inputClass} pl-10`}
                    />
                  </div>
                </div>
              </div>

              <div className="pb-2" />
            </div>
          ) : (
            <div className="space-y-5">
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-600">
                  {error}
                </div>
              )}

              <div>
                <label className={labelClass}>Sponsorship Tier *</label>
                <div className="relative">
                  <SparklesIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <select
                    value={form.sponsorshipTier}
                    onChange={e => f('sponsorshipTier', e.target.value)}
                    className={`${inputClass} pl-10 appearance-none`}
                  >
                    {SPONSORSHIP_TIERS.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <ChevronDownIcon className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
              </div>

              <div className="rounded-2xl border border-gray-100 overflow-hidden">
                <div className="bg-golf-sand/40 px-4 py-3 flex items-center justify-between border-b border-gray-100">
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Selected Tier</p>
                    <span className={`mt-1 inline-block text-xs font-bold px-2.5 py-1 rounded-full border ${tierColor}`}>
                      {form.sponsorshipTier}
                    </span>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-400 mb-0.5">Contribution</p>
                    <p className="text-base font-bold text-golf-gray">
                      {contribution === 0 ? 'Discuss' : `₦${(contribution / 1_000_000).toFixed(0)}M`}
                    </p>
                  </div>
                </div>

                <div className="px-4 py-3">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2.5">Benefits Included</p>
                  <div className="flex flex-wrap gap-1.5">
                    {benefits.map(b => (
                      <span
                        key={b}
                        className="text-xs bg-white border border-gray-200 text-golf-gray rounded-lg px-2.5 py-1 font-medium"
                      >
                        {b}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="bg-golf-sand/30 rounded-xl px-4 py-3 border border-golf-sand/60">
                <p className="text-xs text-gray-500 leading-relaxed">
                  Submitting this form registers your company's interest. Our sponsorship team will reach out within 2 business days to finalize the agreement.
                </p>
              </div>

              <div className="pb-2" />
            </div>
          )}
        </div>

        {!done && (
          <div className="flex gap-3 px-5 pt-4 pb-6 border-t border-gray-100 flex-shrink-0" style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}>
            {step === 2 ? (
              <button
                type="button"
                onClick={() => { setStep(1); setError(''); }}
                className="flex-1 py-3 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 bg-white active:bg-gray-50 transition-colors"
              >
                Back
              </button>
            ) : (
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-3 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 bg-white active:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            )}
            {step === 1 ? (
              <button
                type="button"
                onClick={handleNext}
                className="flex-1 py-3 rounded-xl bg-golf-orange text-white text-sm font-semibold active:bg-golf-orange/90 transition-colors"
              >
                Next: Choose Tier
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="flex-1 py-3 rounded-xl bg-golf-orange text-white text-sm font-semibold active:bg-golf-orange/90 disabled:opacity-60 transition-colors"
              >
                {saving ? 'Submitting...' : 'Submit Interest'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
